/**
 * MessageDetailPanel - Dettagli messaggio con connessioni neurali
 * @updated 2026-01-10
 *
 * Mostra:
 * - Contenuto completo del messaggio
 * - Metadati (timestamp, trascritto, etc.)
 * - Connessioni neurali esistenti
 * - Azioni per creare nuove connessioni
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 */

import { useMemo } from 'react';
import {
  MessageCircle,
  Share2,
  CheckSquare,
  FileText,
  Calendar,
  Link2,
  User,
  Clock,
  Mic,
  ChevronRight,
} from 'lucide-react';
import { DetailPanel, DetailSection, DetailField, DetailActions } from '../common/base';
import type { ChatMessage } from '@business-assistant/shared';
import { useTranslation } from '../../hooks';

// =============================================================================
// TYPES
// =============================================================================

interface MessageDetailPanelProps {
  message: ChatMessage | null;
  isOpen: boolean;
  onClose: () => void;
}

interface NeuralConnection {
  id: string;
  type: 'task' | 'event' | 'note' | 'contact' | 'invoice';
  title: string;
  createdAt: string;
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  messageContent: {
    padding: '16px',
    background: 'var(--bg-secondary, #f9fafb)',
    borderRadius: '12px',
    fontSize: '14px',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  } as React.CSSProperties,
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: 'rgba(139, 92, 246, 0.1)',
    color: 'var(--brand, #8b5cf6)',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--text-muted, #6b7280)',
    fontSize: '13px',
    marginBottom: '8px',
  } as React.CSSProperties,
  actionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
    marginTop: '12px',
  } as React.CSSProperties,
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    background: 'var(--bg, #fff)',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontSize: '13px',
    fontWeight: 500,
  } as React.CSSProperties,
  actionIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,
  connectionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'var(--bg, #fff)',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '10px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  } as React.CSSProperties,
  connectionIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,
  emptyConnections: {
    padding: '24px',
    textAlign: 'center' as const,
    color: 'var(--text-muted, #6b7280)',
    fontSize: '13px',
  } as React.CSSProperties,
  closeBtn: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '8px',
    background: 'var(--brand, #8b5cf6)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    width: '100%',
  } as React.CSSProperties,
};

// =============================================================================
// ACTION CONFIG
// =============================================================================

const QUICK_ACTIONS_CONFIG = [
  {
    key: 'forward',
    icon: <Share2 size={16} />,
    labelKey: 'messageDetail.actions.forward',
    color: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.1)',
  },
  {
    key: 'task',
    icon: <CheckSquare size={16} />,
    labelKey: 'messageDetail.actions.createTask',
    color: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.1)',
  },
  {
    key: 'note',
    icon: <FileText size={16} />,
    labelKey: 'messageDetail.actions.addNote',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.1)',
  },
  {
    key: 'event',
    icon: <Calendar size={16} />,
    labelKey: 'messageDetail.actions.createEvent',
    color: '#22c55e',
    bg: 'rgba(34, 197, 94, 0.1)',
  },
];

// =============================================================================
// COMPONENT
// =============================================================================

export function MessageDetailPanel({ message, isOpen, onClose }: MessageDetailPanelProps) {
  const { t, language } = useTranslation();

  // Build translated quick actions
  const quickActions = useMemo(() =>
    QUICK_ACTIONS_CONFIG.map(action => ({
      ...action,
      label: t(action.labelKey),
    })),
    [t]
  );

  if (!message) return null;

  const isUser = message.role === 'user';

  // TODO: Get real connections from neural connections store
  const connections: NeuralConnection[] = [];

  const handleAction = (action: string) => {
    console.log(`Action: ${action} for message:`, message.id);
    // TODO: Open corresponding modal/widget
  };

  const getConnectionIcon = (type: NeuralConnection['type']) => {
    switch (type) {
      case 'task':
        return <CheckSquare size={16} />;
      case 'event':
        return <Calendar size={16} />;
      case 'note':
        return <FileText size={16} />;
      case 'contact':
        return <User size={16} />;
      case 'invoice':
        return <FileText size={16} />;
      default:
        return <Link2 size={16} />;
    }
  };

  const getConnectionColor = (type: NeuralConnection['type']) => {
    switch (type) {
      case 'task':
        return { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' };
      case 'event':
        return { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' };
      case 'note':
        return { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' };
      case 'contact':
        return { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' };
      case 'invoice':
        return { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
      default:
        return { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' };
    }
  };

  const locale = language === 'it' ? 'it-IT' : 'en-US';

  return (
    <DetailPanel
      isOpen={isOpen}
      onClose={onClose}
      title={t('messageDetail.title')}
      subtitle={isUser ? t('messageDetail.messageSent') : t('messageDetail.assistantResponse')}
      icon={<MessageCircle size={18} />}
      headerColor={isUser ? '#8b5cf6' : '#3b82f6'}
      width="50%"
      footer={
        <DetailActions>
          <button style={styles.closeBtn} onClick={onClose}>
            {t('common.close')}
          </button>
        </DetailActions>
      }
    >
      {/* Message Content */}
      <DetailSection title={t('messageDetail.content')}>
        <div style={styles.messageContent}>{message.content}</div>
      </DetailSection>

      {/* Metadata */}
      <DetailSection title={t('messageDetail.information')}>
        <div style={styles.metaRow}>
          <Clock size={14} />
          <span>
            {new Date(message.timestamp).toLocaleString(locale, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </span>
        </div>
        <div style={styles.metaRow}>
          <User size={14} />
          <span>{isUser ? t('messageDetail.you') : t('messageDetail.aiAssistant')}</span>
        </div>
        {message.transcribed && (
          <div style={styles.metaRow}>
            <Mic size={14} />
            <span style={styles.badge}>{t('messageDetail.transcribedFromVoice')}</span>
          </div>
        )}
      </DetailSection>

      {/* Quick Actions */}
      <DetailSection title={t('messageDetail.quickActions')}>
        <div style={styles.actionGrid}>
          {quickActions.map((action) => (
            <button
              key={action.key}
              style={styles.actionBtn}
              onClick={() => handleAction(action.key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = action.color;
                e.currentTarget.style.background = action.bg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--line, #e5e7eb)';
                e.currentTarget.style.background = 'var(--bg, #fff)';
              }}
            >
              <div
                style={{
                  ...styles.actionIcon,
                  background: action.bg,
                  color: action.color,
                }}
              >
                {action.icon}
              </div>
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </DetailSection>

      {/* Neural Connections */}
      <DetailSection title={t('messageDetail.neuralConnections')}>
        {connections.length === 0 ? (
          <div style={styles.emptyConnections}>
            <Link2 size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>{t('messageDetail.noConnections')}</p>
            <p style={{ margin: '4px 0 0', fontSize: '12px' }}>
              {t('messageDetail.useQuickActionsHint')}
            </p>
          </div>
        ) : (
          connections.map((conn) => {
            const colors = getConnectionColor(conn.type);
            return (
              <div
                key={conn.id}
                style={styles.connectionItem}
                onClick={() => console.log('Open connection:', conn.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.color;
                  e.currentTarget.style.background = colors.bg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--line, #e5e7eb)';
                  e.currentTarget.style.background = 'var(--bg, #fff)';
                }}
              >
                <div
                  style={{
                    ...styles.connectionIcon,
                    background: colors.bg,
                    color: colors.color,
                  }}
                >
                  {getConnectionIcon(conn.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '2px' }}>
                    {conn.title}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>
                    {new Date(conn.createdAt).toLocaleDateString(locale)}
                  </div>
                </div>
                <ChevronRight size={16} style={{ color: 'var(--text-muted, #9ca3af)' }} />
              </div>
            );
          })
        )}
      </DetailSection>
    </DetailPanel>
  );
}
